import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { format } from 'date-fns'
import type { UpdateWithAiWidgetData, IWidgetComponentApi } from '@playbooks/action-cards-utils'
import { ActionCard } from '@playbooks/core/src/interfaces/ActionCard'

import { useTranslation } from '../../contexts/localization/Localization'
import generateResponses from '../../services/ai/generateResponses'
import { traverseNodes } from '../../utils/actionCardOperations'
import AiDisclaimer from './aiDisclaimer'
import processPromptReplacements from '../../utils/ai/processPromptReplacements'
import { getAiPlayContext } from '../../utils/ai/getAiPlayContext'
import { type WidgetProps } from './widgetTypes'
import { applyAiResponse } from '../../services/ai/applyAiResponse'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import useComponentsApiRegistry from '../../hooks/useComponentsApiRegistry'
import type { GeneratedPlayResponse } from '@playbooks/core/src/services/BackendClient'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'
import Disclaimer from '../../components/ui/Disclaimer'

interface UpdateWithAiWidgetProps extends WidgetProps {
  widget: UpdateWithAiWidgetData
}

const minAnswerConfidence = 0.2

/**
 * Widget for updating the Action Card using AI model and an arbitrary pre-configured prompt.
 * @story User Story 9721: New Action Card Widget: Update with AI
 */
const UpdateWithAiWidget: FunctionComponent<UpdateWithAiWidgetProps> = ({ widget, context }) => {
  const { data: state, getActualState, update: updateState, elementsWithCode, settings } = useAppContext()
  // Custom hooks
  const playbooksBackendClient = usePlaybooksBackendClient()
  const { text: t } = useTranslation()

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessingFinished, setIsProcessingFinished] = useState(false)

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: handleButtonClick,
    } as IWidgetComponentApi
  })

  /**
   * Handle button click event.
   */
  async function handleButtonClick() {
    if (isLoading) {
      return
    }
    // Update the UI state
    setIsLoading(true)
    setIsProcessingFinished(false)
    // Process replacements in the prompt
    const prompt = await processPromptReplacements({
      prompt: widget.prompt,
      actionCardsData: state,
      elementsWithCode,
      context,
    })
    // Send request to Playbooks Backend Client
    const generatedResponses = await generateResponses({
      playbooksBackendClient,
      prompt,
      actionCardsData: state,
      currentDate: format(new Date(), 'yyyy-MM-dd'),
      ...getAiPlayContext(settings, state),
      fileIds: widget.fileIds,
      includeAiScope: widget.includeAiScope,
    })
    console.log(`Generated responses`, generatedResponses)
    // Apply the responses to the Action Card
    applyAiResponses(generatedResponses, getActualState().data)

    // Update the Action Card state (applied responses, widget value, tags)
    updateState(widget, 'isAcUpdated', true)
    // Update the UI state
    setIsLoading(false)
    setIsProcessingFinished(true)
  }

  return (
    <div>
      <Disclaimer
        disclaimer={{
          title: t('aiDisclaimerTitle'),
          content: t('aiDisclaimerContent'),
        }}
      />
      <CustomButton size="lg" isLoading={isLoading} onClick={handleButtonClick}>
        <>{widget.buttonLabel || t('updateWithAiWidget_defaultButton')}</>
      </CustomButton>
      {isProcessingFinished && widget.isAcUpdated && (
        <div className="text-right mt-3">
          <AiDisclaimer allowReporting={true} />
        </div>
      )}
    </div>
  )
}

/**
 * Apply the AI responses to the Action Card.
 * @param responses Generated responses from the AI model
 * @param actionCard The Action Card to update
 * @returns The number of applied responses
 */
function applyAiResponses(responses: GeneratedPlayResponse[], actionCard: ActionCard) {
  let appliedResponsesCount = 0
  traverseNodes(actionCard, 'id', (node, key) => {
    const response = responses.find(response => response.questionId === node[key])
    if (response && response.confidence >= minAnswerConfidence) {
      console.log('Response found for node', node, response)
      applyAiResponse(response, node)
      appliedResponsesCount++
    }
  })
  return appliedResponsesCount
}

export default UpdateWithAiWidget
